/**
 * Handle Stripe Checkout's return hop on the V2 dashboard (COL-533).
 *
 * Stripe sends the tenant back to `${site}/dashboard?payment=success` (plus a
 * `session_id` when it is a card checkout) or `?payment=canceled`. Until now
 * only V1 could read that: `DashboardRoute` latched any `payment`/`session_id`
 * param to V1, because V1 was the only surface that reconciled the return. So
 * a tenant who paid from the new dashboard — including the deposit-by-card fix
 * that lands them on exactly this URL — was dropped onto the old dashboard.
 *
 * This mirrors `useDocusignSigningReturn`; the same three things matter:
 *
 *  - the payment must be CONFIRMED. Stripe redirects the moment the tenant
 *    pays; our row is written by the webhook, which may not have landed yet.
 *    `confirm-payment-from-session` creates/recovers the row (COL-291), so the
 *    dashboard does not keep showing "unpaid" to someone who has just paid.
 *  - the params must be STRIPPED, or a refresh re-confirms and re-announces a
 *    payment from minutes ago.
 *  - stripping must wait for a user id. If we strip before we can confirm, the
 *    effect never runs the confirm on a slow load and the row is left to the
 *    webhook alone — which is the race this call exists to cover.
 *
 * `payment=canceled` has nothing to reconcile: it is stripped with an info
 * toast so it does not latch the tenant to V1 on the next render.
 */

import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/lib/supabase";

export type PaymentReturnLabels = {
  /** Shown when we confirm the payment landed. */
  success: string;
  /** The tenant backed out of Stripe Checkout. */
  canceled: string;
};

type Options = {
  /** The signed-in user, or null while auth loads. */
  userId: string | null | undefined;
  labels: PaymentReturnLabels;
  /** Toasts, injected so this module does not pick a UI library. */
  notify: {
    success: (message: string) => void;
    info: (message: string) => void;
    error: (message: string) => void;
  };
};

export function useStripePaymentReturn({
  userId,
  labels,
  notify,
}: Options): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  /*
    One handling per return, per mount — the effect depends on searchParams
    and the confirm's own invalidate/strip both change state, so without this
    latch a slow confirm can be started twice.
  */
  const handled = useRef(false);

  /*
    Always strip through the LATEST setSearchParams, never the one captured
    when the confirm started. react-router-dom (6.30.6) hands a functional
    updater its render-time params and replaces the whole query string, so a
    stale setSearchParams rewinds the URL — a tenant who switches tabs while
    the confirm is in flight gets yanked back a second later. The ref makes
    the updater see the CURRENT params and remove only the keys it owns.
  */
  const setSearchParamsRef = useRef(setSearchParams);
  useEffect(() => {
    setSearchParamsRef.current = setSearchParams;
  });

  /*
    The confirm outlives the component if the tenant navigates away. Nothing
    should be announced or rewritten from a dead mount.
  */
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    []
  );

  const payment = searchParams.get("payment");
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (payment !== "success" && payment !== "canceled") return;
    if (handled.current) return;

    const stripParams = () => {
      if (!alive.current) return;
      setSearchParamsRef.current(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("payment");
          next.delete("session_id");
          return next;
        },
        { replace: true }
      );
    };

    const refetchPayments = () =>
      // Prefix match — covers the V2 ledger key
      // `["payments", userId, "tenant-v2-ledger"]` and the proofs key.
      queryClient.invalidateQueries({ queryKey: ["payments"] });

    if (payment === "canceled") {
      handled.current = true;
      notify.info(labels.canceled);
      stripParams();
      return;
    }

    /*
      No user id yet — auth is still loading. Return WITHOUT stripping so this
      runs again when it resolves; `confirm-payment-from-session` needs the
      caller's JWT, and stripping now would leave the row to the webhook alone.
    */
    if (!userId) return;

    handled.current = true;
    const confirm = async () => {
      if (sessionId) {
        try {
          await supabase.functions.invoke("confirm-payment-from-session", {
            body: { session_id: sessionId },
          });
        } catch {
          // Ignore: the webhook may have already recorded it. The refetch
          // below still picks up whatever landed.
        }
      }
      if (alive.current) notify.success(labels.success);
      refetchPayments();
    };
    confirm().finally(stripParams);
    // A payment row can arrive slightly after the confirm returns (the webhook
    // and our recovery race); a second refetch catches the late write without
    // the tenant having to refresh.
    const t = setTimeout(() => refetchPayments(), 2000);
    return () => clearTimeout(t);
    // `setSearchParams` is deliberately NOT a dependency: it changes on every
    // navigation, and the hook reads it through a ref precisely so a change
    // does not re-run (or stale-capture) an in-flight return.
  }, [payment, sessionId, userId, labels, notify, queryClient]);
}
